import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { trackFeature, FEATURE_KEYS } from "../../services/telemetry";
import {
  createFinancialTransaction,
  updateFinancialTransaction,
  deleteFinancialTransaction,
  getTransactionByAppointment,
  getMonthlyTransactionStats,
  getPendingCollectionTransactions,
  getExpectedTransactions,
  getPaidTransactionsForMonth,
  getPatientTransactions,
  getPatientTransactionsSecure,
  createManualIncomeEntry,
  getPatientPendingDebts,
  settleDebtsWithPayment,
  forgiveDebt,
  // New advanced functions
  splitPaymentTransaction,
  updateTransactionStatus,
  confirmAppointmentPayment,
  getAppointmentsWithoutPayments,
  getTransactionsForKanban,
  getTransactionById,
  getTransactionsInRange,
  createIncomeWithLinking,
  isKanbanStatus,
  logActivity,
  ACTIVITY_ACTIONS,
  ENTITY_TYPES,
  TRANSACTION_STATUS,
  sendPaymentSummarySMS,
} from "../../firebase";

// Cross-API invalidation tags for calendarApi when transaction/payment data changes

// Cross-API invalidation for the live Reports page: report month docs update
// via onSnapshot, but the reports LIST (trend charts) is fetch-based and must
// refetch after data mutations.
const CROSS_INVALIDATE_MONTHLY_REPORTS = [{ type: 'MonthlyReport', id: 'LIST' }];

const CROSS_INVALIDATE_CALENDAR = [
  { type: 'Appointments', id: 'LIST' },
  { type: 'Appointments', id: 'PENDING_ACTION' },
  { type: 'Finance', id: 'STATS' },
  { type: 'Finance', id: 'PENDING_PAYMENTS' },
  { type: 'Finance', id: 'UPCOMING' },
];

export const financeApi = createApi({
  reducerPath: "financeApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: [
    "Transactions",
    "TransactionStats",
    "PendingCollection",
    "KanbanTransactions",
    "AppointmentsWithoutPayments",
  ],
  endpoints: (builder) => ({
    // Get monthly transaction statistics
    getMonthlyTransactionStats: builder.query({
      async queryFn({ clinicId, therapistId, year, month, includeAllTherapists = false }) {
        try {
          const stats = await getMonthlyTransactionStats(
            clinicId,
            therapistId,
            year,
            month,
            { includeAllTherapists }
          );
          return { data: stats };
        } catch (error) {
          console.error("[CK-7600] Error fetching monthly transaction stats:", {
            errorCode: "CK-7600",
            errorMessage: error.message,
            clinicId,
            therapistId,
            year,
            month,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "TransactionStats", id: "MONTHLY" }],
    }),

    // Get pending collection transactions (debtors)
    getPendingCollection: builder.query({
      async queryFn({ clinicId, therapistId, limit, includeAllTherapists = false }) {
        try {
          const transactions = await getPendingCollectionTransactions(
            clinicId,
            therapistId,
            limit,
            { includeAllTherapists }
          );
          return { data: transactions };
        } catch (error) {
          console.error("[CK-7601] Error fetching pending collection:", {
            errorCode: "CK-7601",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "PendingCollection", id: "LIST" }],
    }),

    // Single transaction by id — Finance page `?tx=<id>` deep link (Grow notifications)
    getTransactionById: builder.query({
      async queryFn({ clinicId, transactionId }) {
        try {
          const transaction = await getTransactionById(clinicId, transactionId);
          return { data: transaction };
        } catch (error) {
          console.error("[CK-7733] Error fetching transaction by id:", {
            errorCode: "CK-7733",
            errorMessage: error.message,
            clinicId,
            transactionId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { transactionId }) => [{ type: "Transactions", id: transactionId }],
    }),

    // Get expected transactions for a month
    getExpectedTransactions: builder.query({
      async queryFn({ clinicId, therapistId, year, month, includeAllTherapists = false }) {
        try {
          const transactions = await getExpectedTransactions(
            clinicId,
            therapistId,
            year,
            month,
            { includeAllTherapists }
          );
          return { data: transactions };
        } catch (error) {
          console.error("[CK-7602] Error fetching expected transactions:", {
            errorCode: "CK-7602",
            errorMessage: error.message,
            clinicId,
            therapistId,
            year,
            month,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "Transactions", id: "EXPECTED" }],
    }),

    // Get paid transactions for a month (payment history)
    getPaidTransactionsForMonth: builder.query({
      async queryFn({ clinicId, therapistId, year, month, includeAllTherapists = false }) {
        try {
          const transactions = await getPaidTransactionsForMonth(
            clinicId,
            therapistId,
            year,
            month,
            { includeAllTherapists }
          );
          return { data: transactions };
        } catch (error) {
          console.error("[CK-7609] Error fetching paid transactions:", {
            errorCode: "CK-7609",
            errorMessage: error.message,
            clinicId,
            therapistId,
            year,
            month,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "Transactions", id: "PAID_HISTORY" }],
    }),

    // Transactions for a date window (Reports page table) — indexed range
    // queries: created-in-range ∪ paid-in-range, optionally therapist-scoped
    getTransactionsInRange: builder.query({
      async queryFn({ clinicId, start, end, therapistId }) {
        try {
          const transactions = await getTransactionsInRange(clinicId, {
            start,
            end,
            therapistId,
          });
          return { data: transactions };
        } catch (error) {
          console.error("[CK-7917] Error fetching transactions in range:", {
            errorCode: "CK-7917",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "Transactions", id: "RANGE" }],
    }),

    // Get transaction by appointment ID
    getTransactionByAppointment: builder.query({
      async queryFn({ clinicId, appointmentId }) {
        try {
          const transaction = await getTransactionByAppointment(
            clinicId,
            appointmentId
          );
          return { data: transaction };
        } catch (error) {
          console.error(
            "[CK-7603] Error fetching transaction by appointment:",
            {
              errorCode: "CK-7603",
              errorMessage: error.message,
              clinicId,
              appointmentId,
              timestamp: new Date().toISOString(),
            }
          );
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { appointmentId }) => [
        { type: "Transactions", id: appointmentId },
      ],
    }),

    // Create financial transaction
    createTransaction: builder.mutation({
      async queryFn({ clinicId, transactionData, therapistId, therapistName }) {
        try {
          const transaction = await createFinancialTransaction(
            clinicId,
            transactionData
          );

          // Log activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || "לא ידוע",
              action: ACTIVITY_ACTIONS.PAYMENT,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: transactionData.appointmentId,
              entityName: transactionData.patientName,
              details: {
                amount: transactionData.amount,
                status: transactionData.status,
              },
              success: true,
            });
          }

          return {
            data: { ...transaction, patientId: transactionData.patientId },
          };
        } catch (error) {
          console.error("[CK-7604] Error creating transaction:", {
            errorCode: "CK-7604",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result) => {
        const tags = [
          { type: "TransactionStats", id: "MONTHLY" },
          { type: "PendingCollection", id: "LIST" },
          { type: "Transactions", id: "EXPECTED" },
          { type: "Transactions", id: "RANGE" },
          { type: "Transactions", id: "PAID_HISTORY" },
          { type: "KanbanTransactions", id: "LIST" },
        ];

        // If result has patientId, invalidate patient-specific cache
        if ((result?.patientId || result?.data?.patientId)) {
          tags.push({
            type: "Transactions",
            id: `PATIENT_${(result.patientId || result.data?.patientId)}`,
          });
        }

        return tags;
      },
    }),

    // Update financial transaction
    updateTransaction: builder.mutation({
      async queryFn({
        clinicId,
        transactionId,
        updateData,
        therapistId,
        therapistName,
        patientName,
        patientId,
      }) {
        try {
          const transaction = await updateFinancialTransaction(
            clinicId,
            transactionId,
            updateData
          );

          // Log activity for payment updates
          if (therapistId && updateData.status) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || "לא ידוע",
              action: ACTIVITY_ACTIONS.PAYMENT,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: transaction.appointmentId,
              entityName: patientName,
              details: {
                newStatus: updateData.status,
                amount: updateData.amount || transaction.amount,
              },
              success: true,
            });
          }

          // Return transaction with patientId for tag invalidation
          return {
            data: {
              ...transaction,
              patientId: patientId || transaction.patientId,
              newStatus: updateData.status,
            },
          };
        } catch (error) {
          console.error("[CK-7605] Error updating transaction:", {
            errorCode: "CK-7605",
            errorMessage: error.message,
            clinicId,
            transactionId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result) => {
        const tags = [
          { type: "TransactionStats", id: "MONTHLY" },
          { type: "PendingCollection", id: "LIST" },
          { type: "Transactions", id: "EXPECTED" },
          { type: "Transactions", id: "RANGE" },
          { type: "Transactions", id: "PAID_HISTORY" }, // Always invalidate payment history
          { type: "KanbanTransactions", id: "LIST" }, // status edits from profile / calendar modal must refresh the board
        ];

        // If result has patientId, invalidate patient-specific cache
        if ((result?.patientId || result?.data?.patientId)) {
          tags.push({
            type: "Transactions",
            id: `PATIENT_${(result.patientId || result.data?.patientId)}`,
          });
          tags.push({
            type: "PendingCollection",
            id: `PATIENT_${(result.patientId || result.data?.patientId)}`,
          });
        }

        return tags;
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch({ type: 'calendarApi/invalidateTags', payload: CROSS_INVALIDATE_CALENDAR });
          dispatch({ type: 'monthlyReportsApi/invalidateTags', payload: CROSS_INVALIDATE_MONTHLY_REPORTS });
        } catch { /* mutation failed */ }
      },
    }),

    // Delete financial transaction
    deleteTransaction: builder.mutation({
      async queryFn({ clinicId, transactionId, patientId }) {
        try {
          await deleteFinancialTransaction(clinicId, transactionId);
          return { data: { success: true, patientId } };
        } catch (error) {
          console.error("[CK-7606] Error deleting transaction:", {
            errorCode: "CK-7606",
            errorMessage: error.message,
            clinicId,
            transactionId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result) => {
        const tags = [
          { type: "TransactionStats", id: "MONTHLY" },
          { type: "PendingCollection", id: "LIST" },
          { type: "Transactions", id: "EXPECTED" },
          { type: "Transactions", id: "RANGE" },
          { type: "Transactions", id: "PAID_HISTORY" },
          { type: "KanbanTransactions", id: "LIST" },
        ];

        // If result has patientId, invalidate patient-specific cache
        if ((result?.patientId || result?.data?.patientId)) {
          tags.push({
            type: "Transactions",
            id: `PATIENT_${(result.patientId || result.data?.patientId)}`,
          });
          tags.push({
            type: "PendingCollection",
            id: `PATIENT_${(result.patientId || result.data?.patientId)}`,
          });
        }

        return tags;
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch({ type: 'calendarApi/invalidateTags', payload: CROSS_INVALIDATE_CALENDAR });
          dispatch({ type: 'monthlyReportsApi/invalidateTags', payload: CROSS_INVALIDATE_MONTHLY_REPORTS });
        } catch { /* mutation failed */ }
      },
    }),

    // Create manual income entry
    createManualIncome: builder.mutation({
      async queryFn({ clinicId, therapistId, therapistName, incomeData }) {
        try {
          const transaction = await createManualIncomeEntry(
            clinicId,
            therapistId,
            incomeData
          );

          // Log activity
          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.PAYMENT,
            entityType: ENTITY_TYPES.APPOINTMENT,
            entityId: transaction.id,
            entityName: incomeData.patientName || "הכנסה ידנית",
            details: {
              amount: incomeData.amount,
              description: incomeData.description,
              type: "manual",
            },
            success: true,
          });

          return { data: { ...transaction, patientId: incomeData.patientId } };
        } catch (error) {
          console.error("[CK-7607] Error creating manual income:", {
            errorCode: "CK-7607",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result) => {
        const tags = [
          { type: "TransactionStats", id: "MONTHLY" },
          { type: "Transactions", id: "EXPECTED" },
          { type: "Transactions", id: "RANGE" },
          { type: "Transactions", id: "PAID_HISTORY" }, // Manual income is immediately paid
          { type: "KanbanTransactions", id: "LIST" },
        ];

        // If result has patientId, invalidate patient-specific cache
        if ((result?.patientId || result?.data?.patientId)) {
          tags.push({
            type: "Transactions",
            id: `PATIENT_${(result.patientId || result.data?.patientId)}`,
          });
        }

        return tags;
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch({ type: 'calendarApi/invalidateTags', payload: CROSS_INVALIDATE_CALENDAR });
          dispatch({ type: 'monthlyReportsApi/invalidateTags', payload: CROSS_INVALIDATE_MONTHLY_REPORTS });
        } catch { /* mutation failed */ }
      },
    }),

    // Get patient transactions (for patient profile payments tab)
    // When useSecure=true, uses Cloud Function to bypass Firestore rules
    // (needed for co-therapist access to shared patient transactions)
    getPatientTransactions: builder.query({
      async queryFn({ clinicId, patientId, therapistId, limit = 50, useSecure = false }) {
        try {
          const fetcher = useSecure ? getPatientTransactionsSecure : getPatientTransactions;
          const transactions = await fetcher(
            clinicId,
            patientId,
            therapistId,
            limit
          );
          return { data: transactions };
        } catch (error) {
          console.error("[CK-7608] Error fetching patient transactions:", {
            errorCode: "CK-7608",
            errorMessage: error.message,
            clinicId,
            patientId,
            useSecure,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { patientId }) => [
        { type: "Transactions", id: `PATIENT_${patientId}` },
      ],
    }),

    // Get patient's pending debts (for debt settlement in income modal)
    getPatientPendingDebts: builder.query({
      async queryFn({ clinicId, patientId, therapistId }) {
        try {
          const debts = await getPatientPendingDebts(
            clinicId,
            patientId,
            therapistId
          );
          return { data: debts };
        } catch (error) {
          console.error("[CK-7610] Error fetching patient pending debts:", {
            errorCode: "CK-7610",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { patientId }) => [
        { type: "PendingCollection", id: `PATIENT_${patientId}` },
        { type: "PendingCollection", id: "LIST" },
      ],
    }),

    // Settle debts with a payment
    settleDebtsWithPayment: builder.mutation({
      async queryFn({
        clinicId,
        therapistId,
        therapistName,
        paymentData,
        selectedDebts,
      }) {
        try {
          const result = await settleDebtsWithPayment(
            clinicId,
            therapistId,
            paymentData,
            selectedDebts
          );

          // Log activity
          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.PAYMENT,
            entityType: ENTITY_TYPES.APPOINTMENT,
            entityId: result.payment.id,
            entityName: paymentData.patientName || "תשלום כנגד חוב",
            details: {
              amount: paymentData.amount,
              debtsSettled: selectedDebts.length,
              type: "debt_settlement",
            },
            success: true,
          });

          return { data: { ...result, patientId: paymentData.patientId } };
        } catch (error) {
          console.error("[CK-7611] Error settling debts with payment:", {
            errorCode: "CK-7611",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result) => {
        const tags = [
          { type: "TransactionStats", id: "MONTHLY" },
          { type: "PendingCollection", id: "LIST" },
          { type: "Transactions", id: "EXPECTED" },
          { type: "Transactions", id: "RANGE" },
          { type: "Transactions", id: "PAID_HISTORY" },
        ];

        if ((result?.patientId || result?.data?.patientId)) {
          tags.push({
            type: "Transactions",
            id: `PATIENT_${(result.patientId || result.data?.patientId)}`,
          });
          tags.push({
            type: "PendingCollection",
            id: `PATIENT_${(result.patientId || result.data?.patientId)}`,
          });
        }

        return tags;
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch({ type: 'calendarApi/invalidateTags', payload: CROSS_INVALIDATE_CALENDAR });
          dispatch({ type: 'monthlyReportsApi/invalidateTags', payload: CROSS_INVALIDATE_MONTHLY_REPORTS });
        } catch { /* mutation failed */ }
      },
    }),

    // Forgive a debt (write off without payment)
    forgiveDebt: builder.mutation({
      async queryFn({
        clinicId,
        transactionId,
        reason,
        therapistId,
        therapistName,
        patientName,
        patientId,
        amount,
      }) {
        try {
          const result = await forgiveDebt(clinicId, transactionId, reason);

          // Log activity
          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.DELETE,
            entityType: ENTITY_TYPES.APPOINTMENT,
            entityId: transactionId,
            entityName: patientName || "חוב נמחק",
            details: {
              amount,
              reason: reason || "לא צוינה סיבה",
              type: "debt_forgiveness",
            },
            success: true,
          });

          return { data: { ...result, patientId } };
        } catch (error) {
          console.error("[CK-7612] Error forgiving debt:", {
            errorCode: "CK-7612",
            errorMessage: error.message,
            clinicId,
            transactionId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result) => {
        const tags = [
          { type: "TransactionStats", id: "MONTHLY" },
          { type: "PendingCollection", id: "LIST" },
          { type: "Transactions", id: "EXPECTED" },
          { type: "Transactions", id: "RANGE" },
          { type: "KanbanTransactions", id: "LIST" },
        ];

        if ((result?.patientId || result?.data?.patientId)) {
          tags.push({
            type: "Transactions",
            id: `PATIENT_${(result.patientId || result.data?.patientId)}`,
          });
          tags.push({
            type: "PendingCollection",
            id: `PATIENT_${(result.patientId || result.data?.patientId)}`,
          });
        }

        return tags;
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch({ type: 'calendarApi/invalidateTags', payload: CROSS_INVALIDATE_CALENDAR });
          dispatch({ type: 'monthlyReportsApi/invalidateTags', payload: CROSS_INVALIDATE_MONTHLY_REPORTS });
        } catch { /* mutation failed */ }
      },
    }),

    // ============================================
    // NEW ADVANCED FINANCIAL ENDPOINTS
    // ============================================

    // Get transactions for kanban display
    getTransactionsForKanban: builder.query({
      async queryFn({ clinicId, therapistId, includeAllTherapists = false }) {
        try {
          const grouped = await getTransactionsForKanban(clinicId, therapistId, { includeAllTherapists });
          return { data: grouped };
        } catch (error) {
          console.error("[CK-7620] Error fetching kanban transactions:", {
            errorCode: "CK-7620",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "KanbanTransactions", id: "LIST" }],
    }),

    // Get appointments without linked payments
    getAppointmentsWithoutPayments: builder.query({
      async queryFn({ clinicId, therapistId, patientId, limit = 20, includeAllTherapists = false }) {
        try {
          const appointments = await getAppointmentsWithoutPayments(
            clinicId,
            therapistId,
            patientId,
            limit,
            { includeAllTherapists }
          );
          return { data: appointments };
        } catch (error) {
          console.error("[CK-7621] Error fetching appointments without payments:", {
            errorCode: "CK-7621",
            errorMessage: error.message,
            clinicId,
            therapistId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { patientId }) => [
        { type: "AppointmentsWithoutPayments", id: patientId || "ALL" },
      ],
    }),

    // Split a payment transaction (partial payment)
    splitPaymentTransaction: builder.mutation({
      async queryFn({
        clinicId,
        transactionId,
        paidAmount,
        paymentMethod,
        therapistId,
        therapistName,
        patientName,
        patientId,
      }) {
        try {
          const result = await splitPaymentTransaction(
            clinicId,
            transactionId,
            paidAmount,
            paymentMethod,
            therapistId
          );

          // Log activity
          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.PAYMENT,
            entityType: ENTITY_TYPES.APPOINTMENT,
            entityId: transactionId,
            entityName: patientName || "תשלום חלקי",
            details: {
              paidAmount,
              type: "split_payment",
              originalId: transactionId,
              paidTransactionId: result.paidTransaction.id,
              pendingTransactionId: result.pendingTransaction.id,
            },
            success: true,
          });

          return { data: { ...result, patientId } };
        } catch (error) {
          console.error("[CK-7622] Error splitting payment:", {
            errorCode: "CK-7622",
            errorMessage: error.message,
            clinicId,
            transactionId,
            paidAmount,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result) => {
        const tags = [
          { type: "TransactionStats", id: "MONTHLY" },
          { type: "PendingCollection", id: "LIST" },
          { type: "Transactions", id: "EXPECTED" },
          { type: "Transactions", id: "RANGE" },
          { type: "Transactions", id: "PAID_HISTORY" },
          { type: "KanbanTransactions", id: "LIST" },
        ];

        if ((result?.patientId || result?.data?.patientId)) {
          tags.push({
            type: "Transactions",
            id: `PATIENT_${(result.patientId || result.data?.patientId)}`,
          });
          tags.push({
            type: "PendingCollection",
            id: `PATIENT_${(result.patientId || result.data?.patientId)}`,
          });
        }

        return tags;
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch({ type: 'calendarApi/invalidateTags', payload: CROSS_INVALIDATE_CALENDAR });
          dispatch({ type: 'monthlyReportsApi/invalidateTags', payload: CROSS_INVALIDATE_MONTHLY_REPORTS });
        } catch { /* mutation failed */ }
      },
    }),

    // Update transaction status (for kanban drag-drop and status changes)
    updateTransactionStatus: builder.mutation({
      async queryFn({
        clinicId,
        transactionId,
        newStatus,
        additionalData,
        therapistId,
        therapistName,
        patientName,
        patientId,
      }) {
        try {
          const result = await updateTransactionStatus(
            clinicId,
            transactionId,
            newStatus,
            additionalData || {}
          );

          trackFeature(FEATURE_KEYS.FINANCE_ACTIONS);

          // Log activity
          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.PAYMENT,
            entityType: ENTITY_TYPES.APPOINTMENT,
            entityId: transactionId,
            entityName: patientName || "עדכון סטטוס",
            details: {
              newStatus,
              type: "status_change",
            },
            success: true,
          });

          return { data: { ...result, patientId, newStatus } };
        } catch (error) {
          console.error("[CK-7623] Error updating transaction status:", {
            errorCode: "CK-7623",
            errorMessage: error.message,
            clinicId,
            transactionId,
            newStatus,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result) => {
        const tags = [
          { type: "TransactionStats", id: "MONTHLY" },
          { type: "PendingCollection", id: "LIST" },
          { type: "Transactions", id: "EXPECTED" },
          { type: "Transactions", id: "RANGE" },
          { type: "Transactions", id: "PAID_HISTORY" },
          { type: "KanbanTransactions", id: "LIST" },
        ];

        if ((result?.patientId || result?.data?.patientId)) {
          tags.push({
            type: "Transactions",
            id: `PATIENT_${(result.patientId || result.data?.patientId)}`,
          });
          tags.push({
            type: "PendingCollection",
            id: `PATIENT_${(result.patientId || result.data?.patientId)}`,
          });
        }

        return tags;
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch({ type: 'calendarApi/invalidateTags', payload: CROSS_INVALIDATE_CALENDAR });
          dispatch({ type: 'monthlyReportsApi/invalidateTags', payload: CROSS_INVALIDATE_MONTHLY_REPORTS });
        } catch { /* mutation failed */ }
      },
    }),

    // Create income with smart linking
    createIncomeWithLinking: builder.mutation({
      async queryFn({
        clinicId,
        therapistId,
        therapistName,
        incomeData,
        linkToTransactionId,
      }) {
        try {
          const result = await createIncomeWithLinking(
            clinicId,
            therapistId,
            incomeData,
            linkToTransactionId
          );

          // Log activity
          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.PAYMENT,
            entityType: ENTITY_TYPES.APPOINTMENT,
            entityId: result.id || result.paidTransaction?.id,
            entityName: incomeData.patientName || "הכנסה",
            details: {
              amount: incomeData.amount,
              status: incomeData.status || TRANSACTION_STATUS.PAID,
              linkedTo: linkToTransactionId || null,
              type: linkToTransactionId ? "linked_income" : "new_income",
            },
            success: true,
          });

          return { data: { ...result, patientId: incomeData.patientId } };
        } catch (error) {
          console.error("[CK-7624] Error creating income with linking:", {
            errorCode: "CK-7624",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result) => {
        const tags = [
          { type: "TransactionStats", id: "MONTHLY" },
          { type: "PendingCollection", id: "LIST" },
          { type: "Transactions", id: "EXPECTED" },
          { type: "Transactions", id: "RANGE" },
          { type: "Transactions", id: "PAID_HISTORY" },
          { type: "KanbanTransactions", id: "LIST" },
          { type: "AppointmentsWithoutPayments", id: "ALL" },
        ];

        if ((result?.patientId || result?.data?.patientId)) {
          tags.push({
            type: "Transactions",
            id: `PATIENT_${(result.patientId || result.data?.patientId)}`,
          });
          tags.push({
            type: "PendingCollection",
            id: `PATIENT_${(result.patientId || result.data?.patientId)}`,
          });
          tags.push({
            type: "AppointmentsWithoutPayments",
            id: (result.patientId || result.data?.patientId),
          });
        }

        return tags;
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch({ type: 'calendarApi/invalidateTags', payload: CROSS_INVALIDATE_CALENDAR });
          dispatch({ type: 'monthlyReportsApi/invalidateTags', payload: CROSS_INVALIDATE_MONTHLY_REPORTS });
        } catch { /* mutation failed */ }
      },
    }),

    // Confirm appointment payment (EXPECTED → PENDING)
    // Called when therapist confirms that an appointment occurred
    confirmAppointmentPayment: builder.mutation({
      async queryFn({
        clinicId,
        appointmentId,
        therapistId,
        therapistName,
        patientName,
        patientId,
      }) {
        try {
          const result = await confirmAppointmentPayment(clinicId, appointmentId);

          if (result) {
            // Log activity
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || "לא ידוע",
              action: ACTIVITY_ACTIONS.PAYMENT,
              entityType: ENTITY_TYPES.APPOINTMENT,
              entityId: appointmentId,
              entityName: patientName || "אישור פגישה",
              details: {
                type: "appointment_confirmed",
                transactionId: result.id,
              },
              success: true,
            });
          }

          return { data: { ...result, patientId } };
        } catch (error) {
          console.error("[CK-7625] Error confirming appointment payment:", {
            errorCode: "CK-7625",
            errorMessage: error.message,
            clinicId,
            appointmentId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result) => {
        const tags = [
          { type: "TransactionStats", id: "MONTHLY" },
          { type: "PendingCollection", id: "LIST" },
          { type: "Transactions", id: "EXPECTED" },
          { type: "Transactions", id: "RANGE" },
          { type: "KanbanTransactions", id: "LIST" },
        ];

        if ((result?.patientId || result?.data?.patientId)) {
          tags.push({
            type: "Transactions",
            id: `PATIENT_${(result.patientId || result.data?.patientId)}`,
          });
        }

        return tags;
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch({ type: 'calendarApi/invalidateTags', payload: CROSS_INVALIDATE_CALENDAR });
          dispatch({ type: 'monthlyReportsApi/invalidateTags', payload: CROSS_INVALIDATE_MONTHLY_REPORTS });
        } catch { /* mutation failed */ }
      },
    }),

    // Send a payment summary SMS for selected pending transactions.
    // Server-side: status validation, monthly quota, 15-min per-(therapist,
    // patient) cooldown, clinic template config persistence. Sending an SMS
    // mutates no transaction data — no cache invalidation needed.
    sendPaymentSummarySms: builder.mutation({
      async queryFn({
        clinicId,
        patientId,
        transactionIds,
        mode,
        freeText,
        config,
        therapistId,
        therapistName,
        patientName,
      }) {
        const result = await sendPaymentSummarySMS({
          clinicId,
          patientId,
          transactionIds,
          mode,
          freeText,
          config,
        });

        logActivity(clinicId, {
          therapistId,
          therapistName: therapistName || "לא ידוע",
          action: "send_payment_summary_sms",
          entityType: ENTITY_TYPES.PATIENT,
          entityId: patientId,
          entityName: patientName || "מטופל",
          details: {
            includedCount: transactionIds?.length || 0,
            mode,
          },
          success: !!result.success,
          ...(result.success ? {} : { errorMessage: result.error }),
        });

        if (!result.success) {
          console.error("[CK-7930] Error sending payment summary SMS:", {
            errorCode: "CK-7930",
            errorMessage: result.error,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: result.error, code: result.code } };
        }

        return { data: result.data };
      },
    }),
  }),
});

export const {
  // Queries
  useGetMonthlyTransactionStatsQuery,
  useGetPendingCollectionQuery,
  useGetExpectedTransactionsQuery,
  useGetPaidTransactionsForMonthQuery,
  useGetTransactionsInRangeQuery,
  useGetTransactionByIdQuery,
  useGetTransactionByAppointmentQuery,
  useGetPatientTransactionsQuery,
  useGetPatientPendingDebtsQuery,
  useLazyGetPatientPendingDebtsQuery,
  // New queries
  useGetTransactionsForKanbanQuery,
  useGetAppointmentsWithoutPaymentsQuery,
  useLazyGetAppointmentsWithoutPaymentsQuery,
  // Mutations
  useCreateTransactionMutation,
  useUpdateTransactionMutation,
  useDeleteTransactionMutation,
  useCreateManualIncomeMutation,
  useSettleDebtsWithPaymentMutation,
  useForgiveDebtMutation,
  // New mutations
  useSplitPaymentTransactionMutation,
  useUpdateTransactionStatusMutation,
  useCreateIncomeWithLinkingMutation,
  useConfirmAppointmentPaymentMutation,
  useSendPaymentSummarySmsMutation,
} = financeApi;
